/**
 * SweepEmptiesPopover — Wave 6.2 small confirmation popover for the
 * "Sweep empties" action on the RaoLanding header.
 *
 * Behavior:
 *   1. On open it calls `sweepEmptyFsrDrafts({ olderThanMinutes, countOnly:true })`
 *      to preview the batch size (delegating the count to the backend rather
 *      than client-side filtering of `/v3/carts/my`).
 *   2. The FSR can tune `olderThanMinutes` (default 60).
 *   3. "Sweep N carts" issues the actual sweep; the parent removes the
 *      affected cards on success.
 *   4. If preview returns 0, the action is disabled with a tooltip.
 *
 * No new colors / no inline color literals beyond design tokens.
 */

import { useEffect, useState } from 'react';
import { Button, ModalShell } from '@/components/primitives';
import type { SweepEmptyFsrDraftsResponse } from '@retail-ops/shared-types';
import { useApi } from '../hooks/useApi';

export interface SweepEmptiesPopoverProps {
  open: boolean;
  onClose: () => void;
  /** Called with the response (parent removes cards + toasts). */
  onSwept: (res: SweepEmptyFsrDraftsResponse) => void;
}

type PreviewState =
  | { kind: 'loading' }
  | { kind: 'ready'; count: number }
  | { kind: 'error'; message: string };

const DEFAULT_MINUTES = 60;
const MIN_MINUTES = 0;
const MAX_MINUTES = 1440;

export function SweepEmptiesPopover({
  open,
  onClose,
  onSwept,
}: SweepEmptiesPopoverProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const [olderThanMinutes, setOlderThanMinutes] = useState<number>(DEFAULT_MINUTES);
  const [preview, setPreview] = useState<PreviewState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Re-preview whenever the popover is opened or `olderThanMinutes` changes.
  useEffect(() => {
    if (!open) return;
    setPreview({ kind: 'loading' });
    const signal = makeSignal();
    client.retail.carts
      .sweepEmptyFsrDrafts({ olderThanMinutes, countOnly: true }, { signal })
      .then(
        (res) => {
          if (!res.success) {
            setPreview({ kind: 'error', message: res.error ?? 'Unable to count drafts.' });
            return;
          }
          // Backend returns `swept` / `cartIds`; older clients used
          // `sweptCount`. Coerce defensively so the preview never
          // renders the literal string "undefined" (Bill 2026-04-26).
          const r = res as unknown as Record<string, unknown>;
          const rawCount = r.sweptCount ?? r.swept ??
            (Array.isArray(r.cartIds) ? r.cartIds.length : 0);
          const count = typeof rawCount === 'number' && Number.isFinite(rawCount)
            ? rawCount
            : 0;
          setPreview({ kind: 'ready', count });
        },
        (err: unknown) => {
          if (err instanceof Error && err.name === 'AbortError') return;
          setPreview({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Unable to count drafts.',
          });
        },
      );
    // client + makeSignal are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, olderThanMinutes]);

  async function handleSweep(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const res = await client.retail.carts.sweepEmptyFsrDrafts(
        { olderThanMinutes, countOnly: false },
        { signal: makeSignal() },
      );
      if (!res.success) {
        setError(res.error ?? 'Unable to sweep drafts.');
        setSubmitting(false);
        return;
      }
      onSwept(res);
      onClose();
    } catch (err: unknown) {
      if (!(err instanceof Error && err.name === 'AbortError')) {
        setError(err instanceof Error ? err.message : 'Unable to sweep drafts.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const count = preview.kind === 'ready' ? preview.count : 0;
  const canSweep = preview.kind === 'ready' && count > 0 && !submitting;

  const minutesId = 'sweep-empties-minutes';

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Sweep empty drafts"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSweep();
            }}
            disabled={!canSweep}
            title={count === 0 ? 'No empty drafts to sweep.' : undefined}
          >
            {submitting
              ? 'Sweeping…'
              : count === 0
                ? 'Nothing to sweep'
                : `Sweep ${count} cart${count === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
        Closes your FSR drafts (<strong>fsr-self</strong> + <strong>fsr-inbranch</strong>) that
        have no applicant captured and no regulated documents attached. Closed drafts are kept
        for the configured retention window in case you need to restore one.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label
          htmlFor={minutesId}
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            color: 'var(--ink-500)',
          }}
        >
          Older than
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <input
            id={minutesId}
            type="number"
            min={MIN_MINUTES}
            max={MAX_MINUTES}
            value={olderThanMinutes}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (!Number.isFinite(n)) return;
              const clamped = Math.max(MIN_MINUTES, Math.min(MAX_MINUTES, Math.round(n)));
              setOlderThanMinutes(clamped);
            }}
            style={{
              width: 96,
              padding: 'var(--sp-3) var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-sm)',
              fontSize: 13,
            }}
          />
          <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>minutes</span>
        </div>
      </div>

      <div
        style={{
          padding: 'var(--sp-4)',
          background: 'var(--info-bg)',
          color: 'var(--info-text)',
          borderRadius: 'var(--r-md)',
          fontSize: 12,
        }}
      >
        {preview.kind === 'loading' && 'Counting drafts…'}
        {preview.kind === 'ready' &&
          (preview.count === 0
            ? 'No empty drafts qualify for sweeping right now.'
            : `${preview.count} empty draft${preview.count === 1 ? '' : 's'} will be closed as Cancelled.`)}
        {preview.kind === 'error' && `Could not count drafts: ${preview.message}`}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-4)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
    </ModalShell>
  );
}
